import * as React from "react";
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField } from "@mui/material";
import { Localized } from "@fluent/react";
import { SaveButton } from "../../../app/components/SaveButton";
import { CancelButton } from "../../../app/components/CancelButton";
import { ComputerizedSystem, updateComputerizedSystemVendorInfo } from "../computerizedSystemsApi";
import { useAppDispatch } from "../../../app/hooks";
import { showError, showSuccess } from "../../notifications/notificationsSlice";

export const EditComputerizedSystemVendorDialog = ({
    computerizedSystem,
    onCancel,
    onUpdate,
}: {
    computerizedSystem: ComputerizedSystem;
    onCancel: () => void;
    onUpdate: (updated: ComputerizedSystem) => void;
}) => {
    const dispatch = useAppDispatch();
    const [softwareVersion, setSoftwareVersion] = React.useState(computerizedSystem.softwareVersion);
    const [touched, setTouched] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [serverError, setServerError] = React.useState<string | null>(null);

    const verErr = touched && softwareVersion?.trim() === "";

    const handleSave = async () => {
        setTouched(true);
        setServerError(null);

        if (!softwareVersion?.trim()) return;

        setSaving(true);
        try {
            const res = await dispatch(
                updateComputerizedSystemVendorInfo({
                    id: computerizedSystem.id,
                    vendor: computerizedSystem.vendor?.trim() ?? "",
                    softwareVersion: softwareVersion?.trim() ?? "",
                    supplierId: computerizedSystem.supplierId ?? null,
                })
            );
            if (updateComputerizedSystemVendorInfo.fulfilled.match(res)) {
                const updated = res.payload as ComputerizedSystem;
                dispatch(showSuccess("saved"))
                onUpdate(updated);
            } else {
                let errorMsg = "error"
                if (res.payload && res.payload.kind === 'http') {
                    const problem = res.payload.problem
                    if (problem) {
                        errorMsg = problem.title

                    }
                }
                setServerError(errorMsg)
            }

        } catch (e: any) {
            dispatch(showError("error"))
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={true} onClose={onCancel} maxWidth="sm" fullWidth>
            <DialogTitle
                sx={{
                    bgcolor: (theme) => theme.palette.grey[100],
                    borderBottom: 1,
                    borderColor: "divider",
                    py: 1.5,
                }}
            >
                <Localized id="cs-version-edit">Edytuj wersję</Localized>
            </DialogTitle>

            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} md={12}>
                        <TextField
                            fullWidth
                            required
                            label={<Localized id="cs-software-version">Software version</Localized>}
                            value={softwareVersion}
                            onChange={(e) => setSoftwareVersion(e.target.value)}
                            error={verErr}
                            helperText={verErr ? <span>Wymagane</span> : null}
                            disabled={saving}
                        />
                    </Grid>
                </Grid>

                {serverError && (
                    <Alert sx={{ mt: 1 }} onClose={() => setServerError(null)} severity="error">
                        <Localized id={serverError}>
                            <span>Server error</span>
                        </Localized>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions>
                <SaveButton loading={saving} onClick={handleSave} />
                <CancelButton loading={saving} onClick={onCancel} />
            </DialogActions>
        </Dialog>
    );
};
